import { SupportingWeakeningWidget } from "./supportingWeakeningWidget";
import * as React from "react";
import { Dispatch } from "react";
import { FindingStrength } from "../../util/FindingStrengthCalculator";
import { featureTo2Lines } from "fe-common/logic";
import { Box, Button, IconButton, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { KahunTitle } from "fe-web-components/styles/typography";
import { AgentDiseaseFlags } from "./AgentDiseaseFlags";
import { OrganSystemIcon } from "./organSystemIcon";
import { DescriptionAccordion } from "./common/DescriptionAccordion";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import { QualifiedFeature } from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { Divider } from "fe-web-components/styles/Divider";
import { SubTypes } from "./SubTypes";
import { DiseaseCluster } from "fe-common/hooks/sortFeaturesWithCrs";
import { ResourceCountDisplay } from "fe-web-components/citations/ResourceCountDisplay";

//region [[ Styles ]]
const DiseaseTitle = styled(KahunTitle)({
  fontSize: "18px",
});

const DiseaseSubTitle = styled(KahunTitle)({
  fontSize: "14px",
  flex: 1,
});

const TitleRight = styled("div")({
  display: "flex",
  alignItems: "center",
});

const TopArea = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
});

//endregion [[ Styles ]]

//region [[ Functions ]]

//endregion [[ Functions ]]
export const PatientDiseaseInfo = ({
  diseaseCluster,
  findingPredictiveCalculator,
  articleCount,
  onGotoReferences,
  onSelect,
  selectedCR,
  setSelectedCR,
}: {
  diseaseCluster: DiseaseCluster;
  findingPredictiveCalculator: (cr: DDClinicalReasoning) => FindingStrength[];
  articleCount: number;
  onGotoReferences: () => any;
  onSelect?: Dispatch<QualifiedFeature>; // selection of finding in graph
  selectedCR?: DDClinicalReasoning; // selected subtype
  setSelectedCR?: Dispatch<DDClinicalReasoning>; // selected subtype
}) => {
  const [primary] = featureTo2Lines(diseaseCluster.qf);
  const [subtypeText] = selectedCR
    ? featureTo2Lines(selectedCR.disease[0])
    : [];
  const shownQF = selectedCR?.disease[0] || diseaseCluster.qf;
  const shownCR = selectedCR || diseaseCluster.crs[0];
  const description = shownQF.feature?.description?.text
    ? shownQF.feature.description
    : diseaseCluster.qf.feature.description;

  return (
    <>
      <TopArea>
        <div>
          <DiseaseTitle>{primary}</DiseaseTitle>
          {selectedCR && (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              paddingLeft={"10px"}
              paddingRight={"10px"}
            >
              <DiseaseSubTitle>{"Subtype: " + subtypeText}</DiseaseSubTitle>
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedCR(null);
                  evt({
                    category: CATEGORY.AGENT_DISEASE_CARD,
                    action: ACTION.REMOVE_SUBTYPE,
                    label: subtypeText,
                  });
                }}
              >
                <CloseIcon fontSize={"small"} />
              </IconButton>
            </Box>
          )}
        </div>
        <TitleRight>
          <OrganSystemIcon feature={diseaseCluster.qf} showBackground={true} />
        </TitleRight>
      </TopArea>

      <AgentDiseaseFlags flags={diseaseCluster.flags} />
      <DescriptionAccordion
        description={description?.text}
        onShowMore={(showMore) => {
          evt({
            category: CATEGORY.AGENT_DISEASE_CARD,
            action: showMore ? ACTION.SHOW_MORE : ACTION.SHOW_LESS,
            label: primary,
          });
        }}
      />
      <Divider />
      <SupportingWeakeningWidget
        findingsStrength={findingPredictiveCalculator(shownCR)}
        onSelect={onSelect}
      />
      <SubTypes
        crs={diseaseCluster.crs}
        filteredId={diseaseCluster?.qf?.id}
        selectedCR={selectedCR}
        setSelectedCR={setSelectedCR}
      />
      {articleCount > 0 && (
        <>
          <Divider />
          <Button onClick={onGotoReferences} variant={"text"}>
            <ResourceCountDisplay
              articlesCount={articleCount}
              fullTitle={"REFERENCES"}
            />
          </Button>
        </>
      )}
    </>
  );
};
